import type { McpChatSuggestionInput } from '../types/mcpApiTypes';
import {
  formatDomainAnswerLabel,
  type SkillFlowStep
} from '../components/skill-flow/skillFlow.types';

export type SuggestionDomainOption = {
  slug: string;
  url?: string;
  show_name?: string | null;
};

function suggestionInputToStep(input: McpChatSuggestionInput): SkillFlowStep {
  return {
    id: input.key,
    title: input.label,
    inputType: input.type,
    optional: input.required === false,
    placeholder: input.placeholder,
    minLines: input.minLines ?? null,
    maxLines: input.maxLines ?? null,
    selectionMode: 'single' as const,
    selectionUi: input.selectionUi,
    options: input.options?.map(option => ({
      id: option.id,
      label: option.label
    })),
    defaultSelectedIds: input.defaultSelectedIds ?? undefined
  };
}

/** Consecutive inputs sharing a `group` become one step with several fields. */
export function suggestionInputsToSteps(
  inputs: McpChatSuggestionInput[]
): SkillFlowStep[] {
  const steps: SkillFlowStep[] = [];
  let currentGroup: string | null = null;
  for (const input of inputs) {
    const step = suggestionInputToStep(input);
    const group = input.group?.trim() || null;
    const last = steps[steps.length - 1];
    if (group && group === currentGroup && last?.fields) {
      last.fields.push(step);
      continue;
    }
    currentGroup = group;
    steps.push(
      group
        ? {
            id: `_group_${steps.length}`,
            title: group,
            inputType: 'group',
            fields: [step]
          }
        : step
    );
  }
  // A group with a single input needs no wrapper.
  return steps.map(step =>
    step.fields?.length === 1 ? step.fields[0] : step
  );
}

function isDomainInput(input: McpChatSuggestionInput): boolean {
  return input.type === 'domain' || input.key === 'domain_slug';
}

function formatAnswerValue(
  input: McpChatSuggestionInput | undefined,
  values: string[],
  domains: SuggestionDomainOption[]
): string {
  const raw = values[0] ?? '';
  if (!raw.trim()) return '';
  if (input && isDomainInput(input)) {
    return formatDomainAnswerLabel(raw.trim(), domains);
  }
  if (input?.type === 'select') {
    const selected = raw.trim();
    const label = input.options?.find(option => option.id === selected)?.label;
    return label ? `${label} (${selected})` : selected;
  }
  if (input?.type === 'textarea') {
    return raw
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)
      .join('\n');
  }
  return raw.trim();
}

export function fillPromptTemplate(
  template: string,
  inputs: McpChatSuggestionInput[],
  answers: Record<string, string[]>,
  domains: SuggestionDomainOption[]
): string {
  let result = template;
  for (const input of inputs) {
    const value = formatAnswerValue(
      input,
      answers[input.key] ?? [],
      domains
    );
    result = result.replaceAll(`{{${input.key}}}`, value);
  }
  return result.replace(/\{\{[a-zA-Z0-9_]+\}\}/g, '').trim();
}

export function collectRawValues(
  inputs: McpChatSuggestionInput[],
  answers: Record<string, string[]>,
  domains: SuggestionDomainOption[]
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const input of inputs) {
    const value = (answers[input.key] ?? [])[0]?.trim();
    if (!value) continue;
    out[input.key] = value;
    if (isDomainInput(input)) {
      const matched = domains.find(domain => domain.slug === value);
      const domainName = (matched?.url || '').trim();
      if (domainName) {
        out.domain_name = domainName;
        out.indexer_onboarding_path = `/indexer/onboarding/${encodeURIComponent(value)}/${encodeURIComponent(domainName)}`;
      }
    }
  }
  return out;
}
